import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CopyButton } from 'common/components/CopyButton'
import { GmailIcon } from 'common/icons/GmailIcon'
import { ShareUrlIcon } from 'common/icons/ShareUrlIcon'
import { Facebook } from 'common/icons/socialLinks/Facebook'
import { LinkedIn } from 'common/icons/socialLinks/LinkedIn'
import { Pinterest } from 'common/icons/socialLinks/Pinterest'
import { Reddit } from 'common/icons/socialLinks/Reddit'
import { Snapchat } from 'common/icons/socialLinks/Snapchat'
import { Telegram } from 'common/icons/socialLinks/Telegram'
import { Tumblr } from 'common/icons/socialLinks/Tumblr'
import { Twitter } from 'common/icons/socialLinks/Twitter'
import { WhatsApp } from 'common/icons/socialLinks/WhatsApp'
import { Xing } from 'common/icons/socialLinks/Xing'
import { getGroupInviteLink, getProfileLink } from 'common/utils/dynamicLinks'
import { shareHandlers } from 'common/utils/socialShareService'
import { Modal } from 'features/Modal_V2'
import { createCardShareLink } from 'features/PublicTokens/actions'
import { ShareOptions } from 'features/ShareModal/types'
import { selectHomeTranslations } from 'features/Translations/selectors'
import { withDiscardModal } from 'hocs/withDiscardModal'
import styles from './styles.module.sass'

interface IShareModal {
  isOpen: boolean
  onClose: () => void
  vacancyId?: string,
  specialityId?: string,
  uid: string,
  username?: string,
  modalTitle: string,
  isGroupInvite?: boolean,
  inviteLink: string,
  groupRef?: string,
  shareOptions?: ShareOptions
}

export const ShareModal = ({
  isOpen,
  onClose,
  vacancyId,
  specialityId,
  uid,
  username,
  modalTitle,
  isGroupInvite,
  inviteLink,
  groupRef,
  shareOptions
}: IShareModal) => {
  const dispatch = useDispatch()
  const [shareLink, setShareLink] = useState(inviteLink)
  const [isMounted, setIsMounted] = useState(true)
  const homeTranslations = useSelector(selectHomeTranslations)
  const bodyText = shareOptions?.bodyText || homeTranslations.cardPopupActionsShareGmailBody
  const subject = shareOptions?.subject || ''

  useEffect(() => {
    setIsMounted(true)
    const fetchShareLink = async () => {
      let link
      if (vacancyId || specialityId) {
        link = inviteLink || await dispatch(createCardShareLink(uid, vacancyId, specialityId))
      } else if (isGroupInvite && groupRef) {
        link = inviteLink || getGroupInviteLink(groupRef)
      } else if (uid) {
        link = inviteLink || getProfileLink(uid, username)
      }
      if (isMounted && link) {
        setShareLink(link as string)
      }
    }
    fetchShareLink()

    return () => setIsMounted(false)
  }, [dispatch, uid, vacancyId, specialityId, isGroupInvite, inviteLink])

  const platforms = [
    {
      name: homeTranslations.cardPopupActionsShareFacebookTitle,
      icon: <Facebook />,
      action: () => shareHandlers.Facebook(shareLink)
    },
    {
      name: homeTranslations.cardPopupActionsShareWhatsAppTitle,
      icon: <WhatsApp />,
      action: () => shareHandlers.WhatsApp(bodyText)
    },
    {
      name: homeTranslations.cardPopupActionsShareTelegramTitle,
      icon: <Telegram />,
      action: () => shareHandlers.Telegram(bodyText)
    },
    {
      name: homeTranslations.cardPopupActionsShareGmailTitle,
      icon: <GmailIcon />,
      action: () => shareHandlers.Gmail(bodyText, subject)
    },
    {
      name: homeTranslations.cardPopupActionsShareTwitterTitle,
      icon: <Twitter />,
      action: () => shareHandlers.Twitter(bodyText)
    },
    {
      name: homeTranslations.cardPopupActionsShareLinkedInTitle,
      icon: <LinkedIn />,
      action: () => shareHandlers.LinkedIn(shareLink)
    },
    {
      name: homeTranslations.cardPopupActionsShareRedditTitle,
      icon: <Reddit />,
      action: () => shareHandlers.Reddit(bodyText)
    },
    {
      name: homeTranslations.cardPopupActionsSharePinterestTitle,
      icon: <Pinterest />,
      action: () => shareHandlers.Pinterest(shareLink)
    },
    {
      name: homeTranslations.cardPopupActionsShareXingTitle,
      icon: <Xing />,
      action: () => shareHandlers.Xing(shareLink)
    },
    {
      name: homeTranslations.cardPopupActionsShareSnapchatTitle,
      icon: <Snapchat />,
      action: () => shareHandlers.Snapchat(shareLink)
    },
    {
      name: homeTranslations.cardPopupActionsShareTumblrTitle,
      icon: <Tumblr />,
      action: () => shareHandlers.Tumblr(shareLink)
    }
  ]

  return (
    <div className={styles.shareModalContainer}>
      <Modal
        isOpen={isOpen}
        width={446}
        applyContainerMobileStyles={false}
        title={modalTitle}
        onClose={onClose}
        isFullScreenOnMobile
        containerStyles={styles.containerStyles}
      >
        <div className={styles.shareIconsContainer}>
          <CopyButton
            shareLink={shareLink}
            copyIcon={<ShareUrlIcon />}
            title={homeTranslations.cardPopupActionsShareCopyText}
            copiedTitle={homeTranslations.cardPopupActionsShareCopied}
            titleStyles={styles.copyTitleStyles}
            copyButtonContainer={styles.shareLinkContainer}
            showIconWithCopiedText
          />
          {platforms.map(({ name, icon, action }) => (
            <div key={name} className={styles.iconWithName} onClick={action}>
              {icon}
              {name}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export const ShareModalWithDiscard = withDiscardModal(ShareModal)
